import { useState } from "react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { Check, ArrowRight, Smartphone, Info } from "lucide-react";
import { scrollToId } from "@/lib/api";

const AppCard = ({ plan, index }) => {
  const [spot, setSpot] = useState({ x: 50, y: 50 });
  const [hover, setHover] = useState(false);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    setSpot({
      x: ((e.clientX - r.left) / r.width) * 100,
      y: ((e.clientY - r.top) / r.height) * 100,
    });
  };

  return (
    <Reveal delay={(index % 3) * 0.1}>
      <div
        data-testid={`app-plan-card-${plan.id}`}
        onMouseMove={onMove}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border p-8 transition-all duration-500 ease-out hover:-translate-y-2 ${
          plan.highlight
            ? "glow-card border-[#00F0FF]/50 bg-[#0A0F1C] hover:shadow-[0_24px_80px_-20px_rgba(0,240,255,0.45)]"
            : "border-white/10 bg-[#0A0F1C] hover:border-[#00F0FF]/40 hover:shadow-[0_24px_70px_-24px_rgba(0,240,255,0.3)]"
        }`}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 transition-opacity duration-500"
          style={{
            opacity: hover ? 1 : 0,
            background: `radial-gradient(340px circle at ${spot.x}% ${spot.y}%, rgba(0,240,255,0.13), transparent 65%)`,
          }}
        />
        <div className="relative flex items-center justify-between gap-3">
          <span className={`rounded-full px-3.5 py-1 font-display text-[11px] font-bold uppercase tracking-widest ${
            plan.highlight ? "bg-[#00F0FF] text-[#050811]" : "border border-white/15 text-slate-400"
          }`}>
            {plan.tier}
          </span>
          {plan.id === "mobile-app-mvp" && <Smartphone size={18} className="text-[#00F0FF]" />}
        </div>
        <h3 className="relative mt-5 font-display text-xl font-bold text-white transition-colors duration-300 group-hover:text-[#00F0FF]">
          {plan.name}
        </h3>
        <p className="relative mt-2 min-h-[3rem] text-sm text-slate-400">{plan.tagline}</p>
        <div className="relative mt-6">
          <span
            className="font-display text-4xl font-black tracking-tight text-white transition-transform duration-500 ease-out group-hover:scale-110 group-hover:text-[#00F0FF] group-hover:[text-shadow:0_0_30px_rgba(0,240,255,0.4)] origin-left inline-block"
            data-testid={`app-plan-price-${plan.id}`}
          >
            {plan.price}
          </span>
          <p className="mt-1 text-xs text-slate-500">{plan.price_note}</p>
        </div>
        <p className="relative mt-7 text-xs font-semibold uppercase tracking-[0.25em] text-[#00F0FF]">
          {plan.includes_label}
        </p>
        <ul className="relative mt-4 flex-1 space-y-3">
          {plan.features.map((f, i) => (
            <li
              key={f}
              className="flex items-start gap-3 text-sm text-slate-300 transition-transform duration-300 ease-out group-hover:translate-x-1"
              style={{ transitionDelay: hover ? `${i * 30}ms` : "0ms" }}
            >
              <Check size={16} className="mt-0.5 shrink-0 text-[#00F0FF]" />
              {f}
            </li>
          ))}
        </ul>
        {plan.examples && (
          <p className="relative mt-6 border-t border-white/10 pt-4 text-xs italic leading-relaxed text-slate-500">
            Examples: {plan.examples}
          </p>
        )}
        {plan.note && (
          <p className="relative mt-4 flex items-start gap-2 rounded-xl border border-white/10 bg-white/5 p-3.5 text-xs leading-relaxed text-slate-400">
            <Info size={14} className="mt-0.5 shrink-0 text-[#00F0FF]" />
            {plan.note}
          </p>
        )}
        <button
          data-testid={`app-plan-cta-${plan.id}`}
          onClick={() => scrollToId("contact")}
          className={`relative mt-6 flex items-center justify-center gap-2 overflow-hidden rounded-full px-6 py-3 text-sm font-bold transition-all duration-300 ${
            plan.highlight
              ? "bg-[#00F0FF] text-[#050811] hover:shadow-[0_0_32px_rgba(0,240,255,0.6)]"
              : "border border-white/20 text-white group-hover:border-[#00F0FF]/70 group-hover:bg-[#00F0FF]/10 group-hover:text-[#00F0FF]"
          }`}
        >
          Discuss this app
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
        </button>
      </div>
    </Reveal>
  );
};

export default function AppPlans({ plans }) {
  return (
    <section id="apps" className="bg-[#070B16] py-24 lg:py-32" data-testid="app-plans-section">
      <div className="mx-auto max-w-7xl px-6">
        <Reveal className="mx-auto max-w-3xl text-center">
          <Eyebrow>App Development Plans</Eyebrow>
          <h2 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
            From a simple tool to a full business app.
          </h2>
          <p className="mt-5 text-base text-slate-400 sm:text-lg">
            Fixed, transparent pricing for web apps and mobile app MVPs — every plan is confirmed in
            a written quote after a free consultation.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {plans.map((plan, i) => (
            <AppCard key={plan.id} plan={plan} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
